/**
 * QueryBuilder Store - step slice.
 *
 * Transform-step mutators operating on the active pipeline: add/remove/
 * move/rename plus the per-type field setters (sources, modes, cleanup,
 * append, conditional, copy-structure, define, field_decl, appendstruct,
 * calc, cat). Loop and weighting mutators live in their own slices.
 */
import { StateCreator } from "zustand"
import { QueryBuilderStore, StepSlice } from "../types"
import {
  blankTransformStep,
  containerForTarget,
  dedupePush,
  isAncestorOf,
  locateStep,
  mapStepInTree,
  nextStepName,
  updateActivePipelineSteps,
  updateStep,
} from "../helpers"

export const createStepSlice: StateCreator<
  QueryBuilderStore,
  [["zustand/persist", unknown]],
  [],
  StepSlice
> = (set) => ({
  // ---- Step management (operates on the active pipeline) ----
  addTransformStep: (type, target) => {
    let createdId = ""
    set((state) => {
      const parentLoopId = target?.parentLoopId ?? null
      return updateActivePipelineSteps(state, (steps) => {
        // Append to root or to a specific loop's body, picked by id.
        const dest =
          parentLoopId === null
            ? steps
            : containerForTarget(steps, parentLoopId) ?? steps
        const fresh = blankTransformStep(type, nextStepName(dest))
        createdId = fresh.id
        if (parentLoopId === null) return [...steps, fresh]
        return mapStepInTree(steps, parentLoopId, (s) =>
          s.type === "loop" ? { ...s, body: [...s.body, fresh] } : s
        )
      })
    })
    return createdId
  },
  removeTransformStep: (stepId) =>
    set((state) =>
      updateActivePipelineSteps(state, (steps) => {
        const loc = locateStep(steps, stepId)
        if (!loc) return steps
        if (loc.parentLoopId === null) {
          return steps.filter((s) => s.id !== stepId)
        }
        return mapStepInTree(steps, loc.parentLoopId, (s) =>
          s.type === "loop"
            ? { ...s, body: s.body.filter((b) => b.id !== stepId) }
            : s
        )
      })
    ),
  moveTransformStep: (stepId, target, toIndex) =>
    set((state) =>
      updateActivePipelineSteps(state, (steps) => {
        const targetLoopId = target.parentLoopId
        // Refuse moves into the dragged step itself or any of its
        // descendants — drag-into-self is the canonical "tree corruption"
        // bug for nested DnD.
        if (targetLoopId !== null) {
          if (targetLoopId === stepId) return steps
          if (isAncestorOf(steps, stepId, targetLoopId)) return steps
        }

        const loc = locateStep(steps, stepId)
        if (!loc) return steps

        // Snapshot the moved step, then prune it from its current home.
        const moved = loc.container[loc.index]
        const sameContainer = loc.parentLoopId === targetLoopId

        const removed =
          loc.parentLoopId === null
            ? steps.filter((s) => s.id !== stepId)
            : mapStepInTree(steps, loc.parentLoopId, (s) =>
                s.type === "loop"
                  ? { ...s, body: s.body.filter((b) => b.id !== stepId) }
                  : s
              )

        // For same-container reorders, removing the source shifts the
        // index — adjust the destination to compensate.
        const adjusted =
          sameContainer && toIndex > loc.index ? toIndex - 1 : toIndex

        if (targetLoopId === null) {
          const next = [...removed]
          const clamp = Math.max(0, Math.min(adjusted, next.length))
          next.splice(clamp, 0, moved)
          return next
        }
        return mapStepInTree(removed, targetLoopId, (s) => {
          if (s.type !== "loop") return s
          const next = [...s.body]
          const clamp = Math.max(0, Math.min(adjusted, next.length))
          next.splice(clamp, 0, moved)
          return { ...s, body: next }
        })
      })
    ),
  renameTransformStep: (stepId, name) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        switch (s.type) {
          case "combine_or":
          case "and_combine":
          case "negate":
          case "qtable":
          case "field_decl":
            return { ...s, name }
          case "conditional_assign":
          case "copy_structure":
          case "appendstruct":
          case "calc":
          case "cat":
            return { ...s, target: name }
          case "define":
            return { ...s, symbol: name }
          case "cleanup":
          case "append":
          case "remove":
          case "loop":
          case "weighting":
            // These have no single `name` field — loops rename via
            // `setLoopVariable`, weighting via its dedicated field/dest
            // setters. Generic rename is a no-op here.
            return s
        }
      })
    ),
  addStepSource: (stepId, variable) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        switch (s.type) {
          case "combine_or":
          case "and_combine":
            return { ...s, sources: dedupePush(s.sources, variable) }
          case "append":
            return { ...s, children: dedupePush(s.children, variable) }
          case "remove":
            return { ...s, targets: dedupePush(s.targets, variable) }
          default:
            return s
        }
      })
    ),
  removeStepSource: (stepId, variable) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        switch (s.type) {
          case "combine_or":
          case "and_combine":
            return {
              ...s,
              sources: s.sources.filter((v) => v !== variable),
            }
          case "append":
            return {
              ...s,
              children: s.children.filter((v) => v !== variable),
            }
          case "remove":
            return {
              ...s,
              targets: s.targets.filter((v) => v !== variable),
            }
          default:
            return s
        }
      })
    ),
  setStepSingleSource: (stepId, variable) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        if (s.type === "negate" || s.type === "cleanup") {
          return { ...s, source: variable }
        }
        return s
      })
    ),
  setCleanupMode: (stepId, mode) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "cleanup" ? { ...s, mode } : s
      )
    ),
  setAppendParent: (stepId, parent) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "append" ? { ...s, parent } : s
      )
    ),
  setConditionalValueMode: (stepId, mode) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "conditional_assign" ? { ...s, valueMode: mode } : s
      )
    ),
  setConditionalValueField: (stepId, variable) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "conditional_assign"
          ? { ...s, valueField: variable }
          : s
      )
    ),
  addConditionalCondition: (stepId, variable) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "conditional_assign"
          ? { ...s, conditions: dedupePush(s.conditions, variable) }
          : s
      )
    ),
  removeConditionalCondition: (stepId, variable) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "conditional_assign"
          ? {
              ...s,
              conditions: s.conditions.filter((v) => v !== variable),
            }
          : s
      )
    ),
  setCopyStructureSource: (stepId, source) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "copy_structure" ? { ...s, source } : s
      )
    ),
  setStepMode: (stepId, mode) =>
    set((state) =>
      updateStep(state, stepId, (s) => {
        if (s.type === "negate") {
          if (mode === "create" || mode === "in_place") {
            return { ...s, mode }
          }
          return s
        }
        if (
          s.type === "combine_or" ||
          s.type === "and_combine" ||
          s.type === "calc"
        ) {
          if (mode === "create" || mode === "into_existing") {
            return { ...s, mode }
          }
          return s
        }
        return s
      })
    ),

  setDefineValue: (stepId, value) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "define" ? { ...s, value } : s
      )
    ),
  setFieldDeclType: (stepId, fieldType) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "field_decl" ? { ...s, fieldType } : s
      )
    ),
  setFieldDeclInitialValue: (stepId, value) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "field_decl" ? { ...s, initialValue: value } : s
      )
    ),
  setAppendStructSource: (stepId, source) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "appendstruct" ? { ...s, source } : s
      )
    ),
  setAppendStructAlias: (stepId, alias) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "appendstruct" ? { ...s, alias } : s
      )
    ),

  // ---- CALC step mutators ----
  setCalcFieldType: (stepId, fieldType) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "calc" ? { ...s, fieldType } : s
      )
    ),
  setCalcExpression: (stepId, expression) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "calc" ? { ...s, expression } : s
      )
    ),

  // ---- CAT step mutators ----
  setCatSource: (stepId, source) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "cat" ? { ...s, source } : s
      )
    ),
  // Bulk-replace the category rows — used when a container is dropped and
  // the labels auto-populate from its BOOL children.
  setCatCategories: (stepId, categories) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "cat" ? { ...s, categories: [...categories] } : s
      )
    ),
  setCatCategoryRange: (stepId, index, range) =>
    set((state) =>
      updateStep(state, stepId, (s) =>
        s.type === "cat"
          ? {
              ...s,
              categories: s.categories.map((c, i) =>
                i === index ? { ...c, range } : c
              ),
            }
          : s
      )
    ),
})
